import { useEffect, useMemo, useState } from 'react';
import { Icon } from '@scripsteam/scrips-signal-ds';

/**
 * MiniMonthCalendar — LOCAL STAND-IN for a Signal DS inline calendar (to migrate later).
 *
 * The design's reschedule dialog draws an inline month (design-export `SchedDialogs` `SchedMiniMonth`): a head with the
 * month and previous / next arrows, Su-Sa, the days of the month in a 7-column grid (a past day in the hint ink, the
 * chosen day on the brand blue), and a foot with a "Today" link and the chosen date. The DS exports only `DatePicker`,
 * a field with a POPOVER calendar and no inline mode, so this draws the same calendar inline on the same contract the
 * dialog gave the DatePicker: `value`, `onChange(date)`, `minDate` (days before it cannot be picked). Look:
 * showcase-booking.css `.mini-month*`.
 */
export interface MiniMonthCalendarProps {
  value?: Date;
  onChange: (date: Date) => void;
  /** Days before this date are drawn past and cannot be picked (the dialog passes today, practice time). */
  minDate?: Date;
  /** "Today" for the Today link (defaults to `minDate`, else the device clock). */
  today?: Date;
  /** Accessible name of the grid. */
  label?: string;
}

const DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const sameDay = (a?: Date, b?: Date) =>
  !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** "Sep 20, 2026" (the design's foot). */
export function formatMiniMonthDate(d: Date): string {
  return `${MONTHS_SHORT[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}, ${d.getFullYear()}`;
}

export function MiniMonthCalendar({ value, onChange, minDate, today, label = 'New date' }: MiniMonthCalendarProps) {
  const anchor = value ?? today ?? minDate ?? new Date();
  const [month, setMonth] = useState(() => new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  // A new value from outside (the dialog seeds it on open) shows its month.
  const valueKey = value ? `${value.getFullYear()}-${value.getMonth()}` : '';
  useEffect(() => {
    if (value) setMonth(new Date(value.getFullYear(), value.getMonth(), 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  const min = minDate ? dayStart(minDate) : null;
  const todayDate = dayStart(today ?? minDate ?? new Date());
  const cells = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const count = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const out: (Date | null)[] = Array.from({ length: first.getDay() }, () => null);
    for (let d = 1; d <= count; d += 1) out.push(new Date(month.getFullYear(), month.getMonth(), d));
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [month]);

  const shift = (n: number) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + n, 1));
  const isPast = (d: Date) => !!min && d.getTime() < min.getTime();

  return (
    <div className="mini-month" data-slot="mini-month">
      <div className="mini-month-head">
        <button type="button" className="mini-month-nav mini-month-prev" aria-label="Previous month" onClick={() => shift(-1)}>
          <Icon name="down" size={16} aria-hidden />
        </button>
        <span>
          {MONTHS[month.getMonth()]} {month.getFullYear()}
        </span>
        <button type="button" className="mini-month-nav mini-month-next" aria-label="Next month" onClick={() => shift(1)}>
          <Icon name="down" size={16} aria-hidden />
        </button>
      </div>
      <div className="mini-month-grid" role="grid" aria-label={label}>
        {DOW.map((d) => (
          <span key={d} className="mini-month-dow" aria-hidden>
            {d}
          </span>
        ))}
        {cells.map((d, i) =>
          d ? (
            <button
              key={i}
              type="button"
              className="mini-month-day"
              data-past={isPast(d) || undefined}
              data-selected={sameDay(d, value) || undefined}
              data-today={sameDay(d, todayDate) || undefined}
              aria-pressed={sameDay(d, value)}
              aria-label={formatMiniMonthDate(d)}
              disabled={isPast(d)}
              onClick={() => onChange(d)}
            >
              {d.getDate()}
            </button>
          ) : (
            <span key={i} className="mini-month-day" data-empty aria-hidden />
          ),
        )}
      </div>
      <div className="mini-month-foot">
        <button
          type="button"
          className="mini-month-today"
          disabled={!!min && todayDate.getTime() < min.getTime()}
          onClick={() => {
            setMonth(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1));
            onChange(todayDate);
          }}
        >
          Today
        </button>
        <span data-slot="mini-month-value">{value ? formatMiniMonthDate(value) : '—'}</span>
      </div>
    </div>
  );
}
